import outWardService from "../services/outWardService";
import productService from "../services/productService";
import { formatApiError } from "./apiErrorUtils";

const OUTWARD_PRODUCT_IDS_BATCH_SIZE = 100;
const SEND_DATE_START = "2024-11-01T13:17:58.526Z";
const SEND_DATE_END = "2024-11-16T13:17:58.526Z";

/**
 * Eski SendProductBulk.createRequestBodySendProduct ile birebir: marketPlaceName === seçilen platform adı
 */
export function buildPlaceSettings(
  marketpleacesAccounts,
  ecommerceAccounts,
  selectedPlace
) {
  const selectedPlaceName = selectedPlace?.name ?? "";

  const marketPlaceSettings = (marketpleacesAccounts ?? [])
    .filter((account) => account.marketPlaceName === selectedPlaceName)
    .map((account) => ({
      id: 0,
      userId: account.userId,
      marketPlaceId: account.marketPlaceId || 0,
      marketPlaceName: account.marketPlaceName,
      valueId: account.valueId,
      value: account.value,
      createDate: new Date().toISOString(),
      updateDate: new Date().toISOString(),
      ia: true,
    }));

  const ecommercePlaceSettings = (ecommerceAccounts ?? [])
    .filter((account) => account.ecommercePlaceName === selectedPlaceName)
    .map((account) => ({
      id: 0,
      userId: account.userId,
      ecommercePlaceId: account.ecommercePlaceId || 0,
      ecommercePlaceName: account.ecommercePlaceName,
      valueId: account.valueId,
      value: account.value,
      createDate: new Date().toISOString(),
      updateDate: new Date().toISOString(),
      ia: true,
    }));

  return { marketPlaceSettings, ecommercePlaceSettings };
}

export function validatePlaceSettings(
  marketpleacesAccounts,
  ecommerceAccounts,
  selectedPlace
) {
  const { marketPlaceSettings, ecommercePlaceSettings } = buildPlaceSettings(
    marketpleacesAccounts,
    ecommerceAccounts,
    selectedPlace
  );

  const isEcommerce = selectedPlace?.type === "ecommerce";
  const settings = isEcommerce ? ecommercePlaceSettings : marketPlaceSettings;

  if (!settings.length) {
    return {
      ok: false,
      message: `${selectedPlace?.name ?? "Platform"} için pazaryeri hesap ayarı bulunamadı.`,
    };
  }

  return { ok: true, marketPlaceSettings, ecommercePlaceSettings };
}

export function createRequestBodySendProduct(
  sendOutwardProducts,
  selectedPlace,
  userId,
  marketPlaceSettings,
  ecommercePlaceSettings,
  startDate = SEND_DATE_START,
  endDate = SEND_DATE_END,
  page = 0,
  size = 100
) {
  return {
    userId,
    marketPlaceSetting: marketPlaceSettings,
    ecommercePlaceSettings: ecommercePlaceSettings,
    startDate: startDate || new Date().toISOString(),
    endDate: endDate || new Date().toISOString(),
    page,
    size,
    productRD: [sendOutwardProducts],
  };
}

export function createRequestBodyEcommerceSendProduct(
  sendOutwardProducts,
  selectedPlace,
  userId,
  marketPlaceSettings,
  ecommercePlaceSettings,
  startDate = SEND_DATE_START,
  endDate = SEND_DATE_END,
  page = 0,
  size = 100
) {
  return {
    userId,
    marketPlaceSetting: marketPlaceSettings,
    ecommercePlaceSettings: ecommercePlaceSettings,
    startDate: startDate || new Date().toISOString(),
    endDate: endDate || new Date().toISOString(),
    page,
    size,
    productRD: sendOutwardProducts,
  };
}

/** getOutwardSendProducts yanıtını ürün dizisine çevirir (eski mod ile uyumlu) */
export function normalizeOutwardProductList(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
}

/**
 * Eski sendProductBulk.senProduct ile aynı OutWard gönderim akışı.
 * productService + outWardService kullanır.
 */
export async function sendOutwardProductsLikeClassic({
  productIds,
  selectedPlace,
  userId,
  token,
  marketpleacesAccounts,
  ecommerceAccounts,
  categoryId = 0,
}) {
  if (!productIds?.length) {
    return { outwardCount: 0, requestCount: 0 };
  }

  const validation = validatePlaceSettings(
    marketpleacesAccounts,
    ecommerceAccounts,
    selectedPlace
  );
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  const { marketPlaceSettings, ecommercePlaceSettings } = validation;

  const outwardResponse = await productService.getOutwardSendProducts(
    productIds,
    selectedPlace.id,
    token,
    selectedPlace.type,
    categoryId
  );

  const responseData = normalizeOutwardProductList(outwardResponse);
  if (!responseData.length) {
    throw new Error(
      "Gönderim için ürün detayı alınamadı (getOutwardSendProducts boş döndü)."
    );
  }

  let requestCount = 0;
  const placeName = selectedPlace.name;

  if (placeName === "Ciceksepeti") {
    for (const element of responseData) {
      const requestBody = createRequestBodySendProduct(
        element,
        selectedPlace,
        userId,
        marketPlaceSettings,
        ecommercePlaceSettings,
        SEND_DATE_START,
        SEND_DATE_END
      );
      await outWardService.sendProduct(
        placeName,
        requestBody,
        token,
        selectedPlace.type
      );
      requestCount += 1;
    }
    await new Promise((resolve) => setTimeout(resolve, 5000));
    return { outwardCount: responseData.length, requestCount };
  }

  if (selectedPlace.type === "ecommerce") {
    const groupedProducts = {};
    responseData.forEach((product) => {
      const mainId = product.productMainId;
      if (!groupedProducts[mainId]) groupedProducts[mainId] = [];
      groupedProducts[mainId].push(product);
    });

    for (const mainId of Object.keys(groupedProducts)) {
      const requestBody = createRequestBodyEcommerceSendProduct(
        groupedProducts[mainId],
        selectedPlace,
        userId,
        marketPlaceSettings,
        ecommercePlaceSettings,
        SEND_DATE_START,
        SEND_DATE_END
      );
      await outWardService.sendProduct(
        placeName,
        requestBody,
        token,
        selectedPlace.type
      );
      requestCount += 1;
    }
    return { outwardCount: responseData.length, requestCount };
  }

  for (const element of responseData) {
    const requestBody = createRequestBodySendProduct(
      element,
      selectedPlace,
      userId,
      marketPlaceSettings,
      ecommercePlaceSettings,
      SEND_DATE_START,
      SEND_DATE_END
    );
    await outWardService.sendProduct(
      placeName,
      requestBody,
      token,
      selectedPlace.type
    );
    requestCount += 1;
  }

  return { outwardCount: responseData.length, requestCount };
}

export { formatApiError, OUTWARD_PRODUCT_IDS_BATCH_SIZE };
